import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyDiHhaLHNCwifTcyMoSFFaMw38U83YKiJ8',
  authDomain:'smrt-75b20.firebaseapp.com',
  projectId: 'smrt-75b20',
  storageBucket: 'smrt-75b20.firebasestorage.app',
  messagingSenderId: '712332817341',
  appId: '1:712332817341:web:8aff112402cd1a4d9d18d4',
  measurementId: 'G-KY9MWVH07R',
};

const FIREBASE_VAPID_KEY = 'BNU3BuyWWf8fgGHWyhSGg_eg7aaOBMCbru4sN3bN72BeLq4SvfhlqcsiLk_TYncSzRuZQN929dR3R6bXegpO2NE' || "";
const FIREBASE_SW_CONFIG_MESSAGE = "SMRT_STATS_SET_FIREBASE_CONFIG";

let messagingPromise = null;
let serviceWorkerRegistrationPromise = null;
const SERVICE_WORKER_READY_TIMEOUT_MS = 10000;

const getMissingFirebaseConfigKeys = () => {
  const missingKeys = [];

  if (!FIREBASE_CONFIG.apiKey) missingKeys.push("REACT_APP_FIREBASE_API_KEY");
  if (!FIREBASE_CONFIG.authDomain) {
    missingKeys.push("REACT_APP_FIREBASE_AUTH_DOMAIN");
  }
  if (!FIREBASE_CONFIG.projectId) {
    missingKeys.push("REACT_APP_FIREBASE_PROJECT_ID");
  }
  if (!FIREBASE_CONFIG.messagingSenderId) {
    missingKeys.push("REACT_APP_FIREBASE_MESSAGING_SENDER_ID");
  }
  if (!FIREBASE_CONFIG.appId) missingKeys.push("REACT_APP_FIREBASE_APP_ID");
  if (!FIREBASE_VAPID_KEY) missingKeys.push("REACT_APP_FIREBASE_VAPID_KEY");

  return missingKeys;
};

const isBrowserContext = () =>
  typeof window !== "undefined" && typeof navigator !== "undefined";

const isFirebaseConfigured = () =>
  Boolean(
    FIREBASE_CONFIG.apiKey &&
      FIREBASE_CONFIG.authDomain &&
      FIREBASE_CONFIG.projectId &&
      FIREBASE_CONFIG.messagingSenderId &&
      FIREBASE_CONFIG.appId &&
      FIREBASE_VAPID_KEY
  );

const postConfigToWorker = (registration) => {
  const message = {
    type: FIREBASE_SW_CONFIG_MESSAGE,
    payload: FIREBASE_CONFIG,
  };

  [registration?.active, registration?.waiting, registration?.installing]
    .filter(Boolean)
    .forEach((worker) => {
      worker.postMessage(message);
    });
};

const waitForServiceWorkerActivation = (registration) => {
  if (!registration || registration.active) {
    return Promise.resolve(registration);
  }

  const pendingWorker = registration.installing || registration.waiting;

  if (!pendingWorker) {
    return Promise.resolve(registration);
  }

  return new Promise((resolve) => {
    if (pendingWorker.state === "activated") {
      resolve(registration);
      return;
    }

    let resolved = false;
    let handleStateChange = null;
    const resolveOnce = () => {
      if (!resolved) {
        resolved = true;
        if (handleStateChange) {
          pendingWorker.removeEventListener("statechange", handleStateChange);
        }
        resolve(registration);
      }
    };
    handleStateChange = () => {
      if (pendingWorker.state === "activated") {
        resolveOnce();
      }
    };

    pendingWorker.addEventListener("statechange", handleStateChange);
    setTimeout(resolveOnce, SERVICE_WORKER_READY_TIMEOUT_MS);
  });
};

const resolveActiveServiceWorkerRegistration = async () => {
  const readyRegistration = await navigator.serviceWorker.ready.catch(() => null);

  if (readyRegistration?.active) {
    postConfigToWorker(readyRegistration);
    return readyRegistration;
  }

  return null;
};

const ensureServiceWorkerRegistration = async () => {
  if (!isBrowserContext() || !("serviceWorker" in navigator)) {
    return null;
  }

  if (!serviceWorkerRegistrationPromise) {
    serviceWorkerRegistrationPromise = navigator.serviceWorker
      .register("/firebase-messaging-sw.js")
      .then(async (registration) => {
        await waitForServiceWorkerActivation(registration);
        postConfigToWorker(registration);
        return registration;
      })
      .catch((error) => {
        serviceWorkerRegistrationPromise = null;
        throw error;
      });
  }

  const registration = await serviceWorkerRegistrationPromise;
  postConfigToWorker(registration);

  return registration;
};

const ensureMessaging = async () => {
  if (!isBrowserContext() || !isFirebaseConfigured()) {
    return null;
  }

  const supported = await isSupported().catch(() => false);

  if (!supported) {
    return null;
  }

  if (!messagingPromise) {
    const app = getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG);
    messagingPromise = Promise.resolve(getMessaging(app));
  }

  return messagingPromise;
};

export const requestPushNotificationToken = async () => {
  const missingConfigKeys = getMissingFirebaseConfigKeys();

  if (!isFirebaseConfigured()) {
    if (missingConfigKeys.length > 0) {
      console.warn(
        "[firebaseMessaging] Missing Firebase config keys:",
        missingConfigKeys.join(", ")
      );
    }

    return { status: "not_configured", token: null };
  }

  if (!isBrowserContext() || !("Notification" in window)) {
    return { status: "unsupported", token: null };
  }

  if (Notification.permission === "denied") {
    return { status: "denied", token: null };
  }

  let permission = Notification.permission;

  if (permission !== "granted") {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    return {
      status: permission === "denied" ? "denied" : "dismissed",
      token: null,
    };
  }

  const messaging = await ensureMessaging();

  if (!messaging) {
    return { status: "unsupported", token: null };
  }

  const serviceWorkerRegistration = await ensureServiceWorkerRegistration();
  const activeServiceWorkerRegistration =
    (serviceWorkerRegistration?.active
      ? serviceWorkerRegistration
      : await resolveActiveServiceWorkerRegistration()) ||
    serviceWorkerRegistration;

  if (!activeServiceWorkerRegistration?.active) {
    console.error(
      "[firebaseMessaging] No active Service Worker available for FCM token."
    );
    return { status: "unavailable", token: null };
  }

  const token = await getToken(messaging, {
    vapidKey: FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: activeServiceWorkerRegistration,
  }).catch((error) => {
    console.error("[firebaseMessaging] Failed to get FCM token:", error);
    return null;
  });

  if (!token) {
    return { status: "unavailable", token: null };
  }

  return { status: "granted", token };
};

export const subscribeToForegroundMessages = async (handler) => {
  if (typeof handler !== "function") {
    return () => {};
  }

  const messaging = await ensureMessaging();

  if (!messaging) {
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    handler(payload);
  });
};

export const firebaseMessagingConstants = {
  FIREBASE_SW_CONFIG_MESSAGE,
};
